import { FormEvent, FunctionComponent, useState } from 'react'
import { useNavigate } from 'react-router'
import { Alert } from 'react-bootstrap'

import Page from 'components/ui/page'
import PageHeader from 'components/ui/pageHeader'
import * as api from './api.client'
import { NewHospitalDoctor } from './types'
import { removeAlphabeticCharacteres } from '../../utilities/string.util'
import { AppMessage } from '../../utilities/messages'
import { useHospitalAdmin } from './hospitalAdminContext'
import ManagedHospitalSelect from './components/managedHospitalSelect'
import DoctorForm, { DoctorFormValues } from './components/doctorForm'

const newDoctorInitialState: NewHospitalDoctor = {
  name: '',
  email: '',
  cpf: '',
  phoneNumber: '',
  notifyUserNow: true,
}

const AddDoctor: FunctionComponent = () => {
  const navigate = useNavigate()
  const { selectedHospital, requiresHospitalSelection } = useHospitalAdmin()
  const [doctor, setDoctor] = useState(newDoctorInitialState)
  const [showFormErrors, setShowFormErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [appMessage, setAppMessage] = useState<AppMessage>({ show: false })

  const addDoctor = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formIsValid = event.currentTarget.checkValidity()

    if (!formIsValid) {
      setShowFormErrors(true)
      return
    }

    if (!selectedHospital) {
      setAppMessage({
        show: true,
        success: false,
        message: 'Selecione o hospital antes de adicionar um médico.',
      })
      return
    }

    setSaving(true)

    try {
      await api.addDoctor(selectedHospital.hospitalId, {
        ...doctor,
        cpf: removeAlphabeticCharacteres(doctor.cpf),
        phoneNumber: removeAlphabeticCharacteres(doctor.phoneNumber),
      })

      navigate(`/doctors`, {
        state: {
          newDoctorAdded: true,
          email: doctor.email,
          userNotified: doctor.notifyUserNow,
        },
      })
    } catch (error) {
      setAppMessage({
        show: true,
        success: false,
        message: `Erro ao adicionar o médico: ${(error as any).messages.join(
          ', '
        )}`,
      })
    } finally {
      setSaving(false)
    }
  }

  const handleFormChange = (
    field: keyof DoctorFormValues,
    value: string | boolean
  ) => setDoctor((current) => ({ ...current, [field]: value }))

  return (
    <Page>
      <PageHeader
        back={{ to: '/doctors', label: 'Médicos' }}
        title="Adicionar médico"
      />

      {requiresHospitalSelection && (
        <Alert variant="primary">
          <p className="mb-2">Selecione o hospital para adicionar um médico.</p>
          <ManagedHospitalSelect />
        </Alert>
      )}

      {appMessage.show && (
        <Alert
          variant="danger"
          onClose={() => setAppMessage({ show: false })}
          dismissible
        >
          {appMessage.message}
        </Alert>
      )}

      <DoctorForm
        values={doctor}
        onChange={handleFormChange}
        onSubmit={addDoctor}
        onCancel={() => navigate('/doctors')}
        validated={showFormErrors}
        saving={saving}
        submitDisabled={!selectedHospital}
        showNotifyOption
      />
    </Page>
  )
}

export default AddDoctor
